import {useEffect, useState} from 'react';
import {API_BASE} from '../../utils/apiConfig';
import {normalizeAiPromo} from '../../utils/aiMatch/normalizeAiPromo';
import {AI_PROMO_POLL_ACTIVE_MS, AI_PROMO_POLL_IDLE_MS} from '../../configs/aiMatch';

const checkIfPageVisible = () => document.visibilityState !== 'hidden';

// Ответы опроса по большей части одинаковые: новый объект с теми же полями
// не должен перерисовывать меню каждые 20 секунд
const checkIfSamePromo = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Промо-лобби «Битвы с AI Claude Opus» на сегодня для главного меню: опрос
 * публичного GET /api/ai-promo/today. Пока лобби есть — чаще
 * (AI_PROMO_POLL_ACTIVE_MS: меняются срок старта и статус), без лобби — реже
 * (AI_PROMO_POLL_IDLE_MS). Опрашиваем только видимую вкладку: в фоновой
 * опрос останавливается, а при возвращении на нее данные запрашиваются сразу.
 *
 * Запрос с куками: при AI_PROMO_VISIBILITY=admins сервер показывает лобби
 * только админам, остальным отвечает {lobbyId: null}. Поэтому смена аккаунта
 * (userId) — повод запросить заново.
 *
 * Сбой сети и 5xx оставляют прежние данные (кнопка не мигает от разовой
 * ошибки), а определенный отказ сервера 4xx (старая сборка без эндпоинта) —
 * как «лобби нет».
 *
 * isAiPromoChecked — ответ сервера для этого игрока уже получен: до него
 * aiPromo = null значит «еще не знаем», а не «лобби нет». Сбой сети и 5xx
 * ответом не считаются. Нужен тем, кто решает по отсутствию лобби (разовый
 * анонс события не должен открыться в окне набора, пока ответа нет)
 *
 * @param {Object} [params]
 * @param {string|null} [params.userId] - id вошедшего игрока (null у гостя)
 * @returns {{aiPromo: (Object|null), isAiPromoChecked: boolean}} aiPromo —
 *   результат normalizeAiPromo или null (лобби нет)
 */
export const useAiPromoLobby = ({userId = null} = {}) => {
  const [aiPromo, setAiPromo] = useState(null);
  // Для кого получен последний ответ: при смене аккаунта прежний не в счет
  // (при AI_PROMO_VISIBILITY=admins ответ зависит от игрока)
  const [checkedUserId, setCheckedUserId] = useState(undefined);

  useEffect(() => {
    let isActive = true;
    let timerId = null;
    let controller = null;
    let hasLobby = false;

    const applyPromo = (nextPromo) => {
      hasLobby = Boolean(nextPromo);
      setAiPromo((prevPromo) => (checkIfSamePromo(prevPromo, nextPromo) ? prevPromo : nextPromo));
      setCheckedUserId(userId);
    };

    function scheduleNext() {
      clearTimeout(timerId);
      if (!isActive || !checkIfPageVisible()) return;
      timerId = setTimeout(load, hasLobby ? AI_PROMO_POLL_ACTIVE_MS : AI_PROMO_POLL_IDLE_MS);
    }

    async function load() {
      clearTimeout(timerId);
      controller?.abort();
      const ownController = new AbortController();
      controller = ownController;
      try {
        const res = await fetch(`${API_BASE}/ai-promo/today`, {
          credentials: 'include',
          signal: ownController.signal,
        });
        // 5xx — временный сбой сервера или прокси (перезапуск, перегрузка в
        // 21:00, когда в меню заходят разом): как и при сбое сети, прежние
        // данные не трогаем — иначе кнопка мигнет, а открытое промо закроется
        // и со следующим опросом откроется снова, еще раз со звуком
        if (res.status >= 500) {
          throw new Error(`AI promo lobby HTTP ${res.status}`);
        }
        const data = res.ok ? await res.json() : null;
        if (isActive && controller === ownController) {
          applyPromo(normalizeAiPromo(data));
        }
      } catch (e) {
        if (e?.name !== 'AbortError') {
          console.warn('AI promo lobby fetch error', e);
        }
      } finally {
        // Прерванный запрос (уход вкладки в фон, новый запрос, размонтирование)
        // следующий не планирует — это сделает тот, кто его прервал
        if (isActive && controller === ownController) {
          controller = null;
          scheduleNext();
        }
      }
    }

    const handleVisibilityChange = () => {
      if (checkIfPageVisible()) {
        load();
        return;
      }
      clearTimeout(timerId);
      controller?.abort();
      controller = null;
    };

    if (checkIfPageVisible()) {
      load();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isActive = false;
      clearTimeout(timerId);
      controller?.abort();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userId]);

  return {aiPromo, isAiPromoChecked: checkedUserId === userId};
};
